import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { BRAND } from '../data/mock';
import { useQuote } from '../context/QuoteContext';

const NAV = [
  { label: 'Home',         to: '/',            external: false },
  { label: 'Customize',    to: '/#customize',  external: true },
  { label: 'Products',     to: '/products',    external: false },
  { label: 'Services',     to: '/#services',   external: true },
  { label: 'Celebrities',  to: '/#celebrities',external: true },
  { label: 'Plans',        to: '/#plans',      external: true },
  { label: 'Testimonials', to: '/#testimonials', external: true },
  { label: 'FAQs',         to: '/#faqs',       external: true },
];

const BrandMark = () => (
  <Link to="/" className="inline-flex items-center gap-3 group" data-testid="brand-mark-link">
    <div className="relative w-[84px] h-[84px] rounded-full bg-gradient-to-br from-pink-50 to-white ring-2 ring-pink-100 shadow-soft overflow-hidden flex items-center justify-center transition-transform group-hover:scale-105">
      <img src={BRAND.logo} alt="Mr. Coconetto" className="w-[66px] h-[66px] object-contain" />
    </div>
    <div className="leading-tight text-left">
      <div className="font-script text-pink-brand text-lg sm:text-xl -mb-0.5">Simply Original</div>
      <div className="font-display font-bold text-stone-900 text-base sm:text-lg">Mr. Coconetto</div>
    </div>
  </Link>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openQuote } = useQuote();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      {/* Thin grey top strip */}
      <div className="hidden md:block bg-stone-100 text-stone-600 text-xs border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-pink-brand" /> Delhi (HQ) · Mumbai · Hyderabad</span>
            <a href={`mailto:${BRAND.email}`} className="inline-flex items-center gap-1.5 hover:text-pink-brand transition-colors">
              <Mail className="w-3.5 h-3.5 text-pink-brand" /> {BRAND.email}
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a href={`https://wa.me/${BRAND.phoneRaw}`} target="_blank" rel="noreferrer" className="hover:text-pink-brand transition-colors">WhatsApp Support</a>
          </div>
        </div>
      </div>

      {/* Main nav bar */}
      <div className={`transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-soft' : 'bg-white/85 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-24 gap-4">
            <BrandMark />

            <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
              {NAV.map((link) => {
                const cls = "relative px-3 py-2 text-sm font-medium text-stone-700 hover:text-pink-brand transition-colors group";
                const testId = `nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`;
                const inner = (
                  <>
                    {link.label}
                    <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-pink-brand scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
                  </>
                );
                return link.external ? (
                  <a key={link.label} href={link.to} className={cls} data-testid={testId}>{inner}</a>
                ) : (
                  <Link key={link.label} to={link.to} className={cls} data-testid={testId}>{inner}</Link>
                );
              })}
            </nav>

            <div className="flex items-center gap-2">
              <Button
                onClick={() => openQuote({ source: 'header-cta' })}
                className="hidden sm:inline-flex bg-pink-brand hover:bg-[#BE185D] text-white rounded-full px-6 h-11 shadow-pink-glow font-semibold"
                data-testid="header-request-quote-button"
              >
                Request a Quote
              </Button>
              <a
                href={`tel:${BRAND.phone}`}
                className="hidden md:inline-flex items-center gap-2 rounded-full bg-white text-pink-brand border border-pink-200 hover:bg-pink-50 px-4 h-11 text-sm font-semibold shadow-sm"
                data-testid="header-call-link"
              >
                <Phone className="w-4 h-4" /> {BRAND.phone}
              </a>
              <button
                className="lg:hidden p-2 rounded-md text-stone-700"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="toggle menu"
                data-testid="header-mobile-toggle"
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {mobileOpen && (
            <div className="lg:hidden py-4 border-t border-stone-100">
              <div className="flex flex-col gap-1">
                {NAV.map((link) => {
                  const cls = "px-3 py-2 rounded-md text-stone-700 hover:bg-pink-50 hover:text-pink-brand font-medium";
                  return link.external ? (
                    <a key={link.label} href={link.to} onClick={() => setMobileOpen(false)} className={cls}>{link.label}</a>
                  ) : (
                    <Link key={link.label} to={link.to} onClick={() => setMobileOpen(false)} className={cls}>{link.label}</Link>
                  );
                })}
                <a href={`tel:${BRAND.phone}`} className="px-3 py-2 rounded-md text-pink-brand font-semibold inline-flex items-center gap-2">
                  <Phone className="w-4 h-4" /> {BRAND.phone}
                </a>
                <button
                  onClick={() => { setMobileOpen(false); openQuote({ source: 'header-mobile' }); }}
                  className="mt-2 px-3 py-2.5 rounded-full bg-pink-brand text-white font-semibold text-center"
                  data-testid="header-mobile-request-quote-button"
                >
                  Request a Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
